/**
 * DynamicHomePage — renders the custom homepage from Page Builder JSON.
 * Reads custom_page.sections from the existing usePublicIndex() response.
 * Shown when app_branding.homepage_type === 'custom'.
 */

import React, { useEffect } from 'react';
import DynamicHeader from '../components/DynamicHeader';
import { usePublicIndex } from '../hooks/usePublicIndex';
import { usePublicApp } from '../context/PublicAppContext';
import { applyPageSEO } from '../utils/seoHelper';
import { useTheme } from '../theme/ThemeContext';
import DynamicPageRenderer from '../components/dynamic-page/DynamicPageRenderer';
import { lastSectionOwnsScroll } from '../components/dynamic-page/sectionUtils';
import DynamicFooter, { resolveFooterMode } from '../components/dynamic-page/DynamicFooter';
import { buildFooterHtml } from '../utils/footerHtml';

export default function DynamicHomePage() {
  const { theme } = useTheme();
  const { setAppBranding } = usePublicApp();
  const { data, isLoading, isError } = usePublicIndex();

  useEffect(() => {
    if (data?.app_branding) {
      setAppBranding(data.app_branding);
    }
  }, [data, setAppBranding]);

  // The custom homepage is the only public surface that was not applying its
  // SEO config: PublicIndexPage reads index_config.seo and PublicPageView reads
  // the page's seo, but this component rendered neither, so the tab kept the
  // SPA's default title. Django's /p/ prerender already prefers the home page's
  // seo for crawlers — this keeps the browser in step with it.
  useEffect(() => {
    if (data?.custom_page?.seo) {
      applyPageSEO(data.custom_page.seo, data.app_branding);
    }
  }, [data]);

  const appBranding = data?.app_branding || {};
  const requiresAuth = !!data?.custom_page_requires_auth;
  const sections = data?.custom_page?.sections || [];
  const pageConfig = data?.custom_page?.page_config || {};
  const headerCfg = pageConfig.header || {};
  const footerCfg = pageConfig.footer;

  // Opt-out flag — defaults to true so existing homepages are unaffected.
  // The footer has no equivalent flag: it is already gated by footer.enabled.
  const showHeader = pageConfig.show_header !== false;

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner-border" style={{ color: theme.primary }} />
      </div>
    );
  }

  if (isError) {
    return (
      <>
        {showHeader && <DynamicHeader headerConfig={headerCfg} appBranding={appBranding} />}
        <main>
          <div className="text-center py-5 text-muted">
            <i className="bi bi-exclamation-triangle" style={{ fontSize: 40 }} />
            <p className="mt-3">Homepage could not be loaded.</p>
          </div>
        </main>
      </>
    );
  }

  // Redirect to sign-in with ?next= so the user lands back on this page after login.
  if (requiresAuth) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/sign-in?next=${next}`);
    return null;
  }

  // See PublicPageView: a full-page HTML/CSS last section owns the scrollport,
  // so the footer is injected into that iframe rather than rendered after it.
  const embedFooter    = !!footerCfg?.enabled && lastSectionOwnsScroll(sections);
  const embeddedFooter = embedFooter
    ? buildFooterHtml(footerCfg, theme, resolveFooterMode(footerCfg))
    : '';

  return (
    <>
      {showHeader && <DynamicHeader headerConfig={headerCfg} appBranding={appBranding} />}
      <main>
        {sections.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>No published sections yet. Configure your homepage in the admin panel.</p>
          </div>
        ) : (
          <DynamicPageRenderer
            sections={sections}
            theme={theme}
            appBranding={appBranding}
            embeddedFooterHtml={embeddedFooter}
          />
        )}
      </main>
      {!embedFooter && <DynamicFooter footerCfg={footerCfg} theme={theme} />}
    </>
  );
}
